"use client";

import * as React from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemIcon from "@mui/material/ListItemIcon";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import { CreditCard as CreditCardIcon } from "@phosphor-icons/react/dist/ssr/CreditCard";
import { LockKey as LockKeyIcon } from "@phosphor-icons/react/dist/ssr/LockKey";
import { User as UserIcon } from "@phosphor-icons/react/dist/ssr/User";
import type { User } from "../../../types/user";
import { RouterLink } from "../../core/link";
import { CognitoSignOut } from "./cognito-sign-out";
import { decodeFromBase64 } from "../../../functions/misc";
import useProfileStore, { PrimaryGroups, useGroupStore } from "../../../store/ProfileStore";
import { paths } from "../../../paths";

export interface UserPopoverProps {
  anchorEl: null | Element;
  onClose?: () => void;
  open: boolean;
}

export function UserPopover({ anchorEl, onClose, open }: UserPopoverProps): React.ReactElement {
  const { primaryGroup } = useGroupStore();
  const { primaryProfile, partnerProfile } = useProfileStore();
  const [user, setUser] = React.useState<User>({ id: "", name: "", email: "", partner: false });
  const fetchUserInfo = async () => {
    if (decodeFromBase64(primaryGroup) === PrimaryGroups.Partner) {
      setUser({
        id: partnerProfile?.Id || '',
        name: `${partnerProfile?.FirstName} ${partnerProfile?.LastName}`,
        email: partnerProfile?.Email,
        partner: true
      });
    } else {
      setUser({
        id: primaryProfile?.Id || '',
        name: `${primaryProfile?.FirstName} ${primaryProfile?.LastName}`,
        email: primaryProfile?.Email || '',
      });
    }
  };
  const setAccountUrl = () => {
    var url;
    if (!user.partner) {
      // Client
      url = paths.settings.myaccount.profile;
    }
    else {
      // Partner
      url = paths.partner.settings.myaccount.settings;
    }
    return url;
  }

  React.useEffect(() => {
    fetchUserInfo();
  }, [primaryProfile, partnerProfile]);

  return (
    <Popover anchorEl={anchorEl} anchorOrigin={{ horizontal: "right", vertical: "bottom" }} onClose={onClose} open={Boolean(open)} slotProps={{ paper: { sx: { width: "280px" } } }} transformOrigin={{ horizontal: "right", vertical: "top" }}>
      <Box sx={{ p: 2 }}>
        <Typography>{user.name}</Typography>
        <Typography color="text.secondary" variant="body2">
          {user.email}
        </Typography>
      </Box>
      <Divider />
      <List sx={{ p: 1 }}>
        <MenuItem component={RouterLink} href={setAccountUrl()} onClick={onClose}>
          <ListItemIcon>
            <UserIcon />
          </ListItemIcon>
          Account
        </MenuItem>
        <MenuItem component={RouterLink} href={"/settings/security"} onClick={onClose}>
          <ListItemIcon>
            <LockKeyIcon />
          </ListItemIcon>
          Security
        </MenuItem>
        {!user.partner &&
          <MenuItem component={RouterLink} href={"/settings/billing"} onClick={onClose}>
            <ListItemIcon>
              <CreditCardIcon />
            </ListItemIcon>
            Billing
          </MenuItem>}
      </List>
      <Divider />
      <Box sx={{ p: 1 }}>
        <CognitoSignOut />
      </Box>
    </Popover>
  );
}
